/* eslint-disable react-refresh/only-export-components */
import type { Session, User } from "@supabase/supabase-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { identifyUser, resetUser } from "./feature-flags";
import { identifyCannyUser } from "../canny";
import { supabase } from "../supabase";
import { api } from "../api/api-client";

export interface StaffRoleInfo {
  studioId: string;
  studioName: string;
  role: 'admin' | 'staff';
  permissions: Record<string, boolean>;
}

export interface Profile {
  id: string;
  email: string;
  is_owner: boolean;
  full_name?: string | null;
  username?: string | null;
  venmo_handle?: string | null;
  cashapp_handle?: string | null;
  zelle_handle?: string | null;
  paypal_handle?: string | null;
  instagram_handle?: string | null;
  bio?: string | null;
  phone?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_relationship?: string | null;
  emergency_contact_phone?: string | null;
  date_of_birth?: string | null;
  profile_photo?: string | null;
  pike13_check_completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  staffRoles?: StaffRoleInfo[];
}

export interface AuthContextType {
  user: User | null;
  profile: Profile | null | undefined;
  session: Session | null;
  loading: boolean;
  hasStudioAccess: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    fullName?: string
  ) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Use localStorage for cross-tab profile caching
const PROFILE_CACHE_KEY = "dnceclub_profile_cache";
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface ProfileCache {
  profile: Profile | null;
  timestamp: number;
  userId: string;
}

function getCachedProfile(userId: string): Profile | null {
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) {
      return null;
    }

    const data: ProfileCache = JSON.parse(cached);

    // Check if cache is for the same user
    if (data.userId !== userId) {
      return null;
    }

    // Check if cache is still valid
    const now = Date.now();
    const age = now - data.timestamp;

    if (age > PROFILE_CACHE_TTL) {
      localStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }

    return data.profile;
  } catch {
    return null;
  }
}

function setCachedProfile(userId: string, profile: Profile | null) {
  try {
    const cache: ProfileCache = {
      profile,
      timestamp: Date.now(),
      userId,
    };
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Silently fail if caching fails
  }
}

// Check if there's likely an active session by looking at Supabase's localStorage token.
// No token = definitely no session = skip the loading spinner for first-time visitors.
// Token exists = probably logged in = show loading while we verify.
function hasPersistedSession(): boolean {
  try {
    return localStorage.getItem('dnceclub-auth') !== null;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined); // undefined = loading, null = no profile
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(hasPersistedSession);

  // Use refs to track current values for auth state change handler
  const userRef = useRef<User | null>(null);
  const profileRef = useRef<Profile | null | undefined>(undefined);

  // Track if we're already fetching profile to prevent duplicates
  const profileFetchPromise = useRef<Promise<Profile | null> | null>(null);

  const fetchProfile = useCallback(
    async (userId: string, retryCount = 0): Promise<Profile | null> => {
      const maxRetries = 3;
      const baseDelay = 1000; // 1 second

      // Check if we're already fetching (only on first attempt)
      if (retryCount === 0 && profileFetchPromise.current) {
        return profileFetchPromise.current;
      }

      // Check cache first (only on first attempt)
      if (retryCount === 0) {
        const cached = getCachedProfile(userId);
        if (cached) {
          return cached;
        }
      }

      // Fetch from database with timeout
      const fetchPromise = (async () => {
        try {
          // Create timeout mechanism
          const timeoutMs = 10000 + retryCount * 5000; // Increase timeout on retries

          let timeoutId: NodeJS.Timeout | undefined;
          const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
              reject(
                new Error(
                  `Profile fetch timeout after ${timeoutMs / 1000} seconds`
                )
              );
            }, timeoutMs);
          });

          const queryPromise = supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .single();

          const result = await Promise.race([queryPromise, timeoutPromise])
            .then((r: unknown) => {
              if (timeoutId) clearTimeout(timeoutId);
              return r;
            })
            .catch((e: unknown) => {
              if (timeoutId) clearTimeout(timeoutId);
              if ((e as Error).message?.includes("timeout")) {
                return {
                  data: null,
                  error: { message: "Profile fetch timeout", code: "TIMEOUT" },
                };
              }
              return { data: null, error: e };
            });

          const { data, error } = result as {
            data: Profile | null;
            error: unknown;
          };

          profileFetchPromise.current = null;

          if (error) {
            // Retry with exponential backoff if not at max retries
            const errorCode = (error as { code?: string }).code;
            if (
              retryCount < maxRetries &&
              (errorCode === "TIMEOUT" ||
                errorCode === "PGRST301" ||
                !errorCode)
            ) {
              const delay = baseDelay * Math.pow(2, retryCount);
              await new Promise((resolve) => setTimeout(resolve, delay));
              return fetchProfile(userId, retryCount + 1);
            }

            return null;
          }

          if (!data) {
            return null;
          }

          // Cache the profile
          setCachedProfile(userId, data);
          return data;
        } catch {
          profileFetchPromise.current = null;

          // Retry on network errors
          if (retryCount < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            await new Promise((resolve) => setTimeout(resolve, delay));
            return fetchProfile(userId, retryCount + 1);
          }

          return null;
        }
      })();

      if (retryCount === 0) {
        profileFetchPromise.current = fetchPromise;
      }

      return fetchPromise;
    },
    []
  );

  const fetchStaffRoles = useCallback(async (): Promise<StaffRoleInfo[]> => {
    try {
      const response = await api.get<{ staffRoles: StaffRoleInfo[] }>('/auth/me');
      return response.staffRoles ?? [];
    } catch {
      return [];
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    // Clear cache to force refresh
    localStorage.removeItem(PROFILE_CACHE_KEY);
    const [profileData, staffRoles] = await Promise.all([
      fetchProfile(user.id),
      fetchStaffRoles(),
    ]);
    const enrichedProfile = profileData ? { ...profileData, staffRoles } : profileData;
    setProfile(enrichedProfile);
  }, [user, fetchProfile, fetchStaffRoles]);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      
      try {
        // Add timeout for getSession
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error("[AuthContext] getSession timeout after 20 seconds"));
          }, 20000);
        });
        
        const sessionResult = await Promise.race([sessionPromise, timeoutPromise])
          .catch((error) => {
            console.error("[AuthContext] Failed to get session:", error);
            return { data: { session: null } };
          });
        
        const { data: { session: initialSession } } = sessionResult as { data: { session: Session | null } };

        if (!mounted) {
          return;
        }

        if (initialSession?.user) {
          setSession(initialSession);
          setUser(initialSession.user);
          userRef.current = initialSession.user;
          // Do not block app on profile fetch; kick off in background
          setLoading(false);
          // Clear cache to ensure fresh profile data on page load
          // This prevents using stale is_owner values after role changes
          localStorage.removeItem(PROFILE_CACHE_KEY);
          fetchProfile(initialSession.user.id)
            .then(async (profileData) => {
              if (!mounted) return;
              // Fetch staff roles from backend and merge into profile
              const staffRoles = await fetchStaffRoles();
              const enrichedProfile = profileData ? { ...profileData, staffRoles } : profileData;
              setProfile(enrichedProfile);
              profileRef.current = enrichedProfile;
              // Identify user in PostHog for analytics and feature flags
              if (profileData) {
                identifyUser(profileData.id, {
                  email: profileData.email,
                  is_owner: profileData.is_owner,
                  full_name: profileData.full_name ?? null,
                });
                identifyCannyUser({
                  id: profileData.id,
                  email: profileData.email,
                  fullName: profileData.full_name ?? "",
                  avatarURL: profileData.profile_photo,
                  createdAt: profileData.created_at,
                });
              }
            })
            .catch((error) => {
              if (!mounted) return;
              console.error("[AuthContext] Failed to fetch profile:", error);
              setProfile(null);
            });
        } else {
          // No session
          setSession(null);
          setUser(null);
          setProfile(null);
          userRef.current = null;
          profileRef.current = null;
          setLoading(false);
        }
      } catch (error) {
        console.error("[AuthContext] Critical error during initialization:", error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) {
        return;
      }

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        if (newSession?.user) {
          // Only fetch profile if it's a different user or we don't have it - using refs for current values
          const currentUserId = userRef.current?.id;
          const currentProfile = profileRef.current;
          const isNewUser = newSession.user.id !== currentUserId;
          const shouldFetchProfile =
            isNewUser ||
            currentProfile === undefined ||
            currentProfile === null;

          // Skip state updates if nothing has changed to prevent unnecessary re-renders
          if (!isNewUser && currentProfile !== undefined && currentProfile !== null) {
            return;
          }

          setSession(newSession);
          setUser(newSession.user);
          userRef.current = newSession.user;
          // Do not block on profile; fetch in background if needed
          setLoading(false);
          if (shouldFetchProfile) {
            // Clear cache on sign-in to ensure fresh profile data
            // This prevents stale is_owner values from being used
            localStorage.removeItem(PROFILE_CACHE_KEY);
            // Only reset profile to undefined for NEW sign-ins, not token refreshes
            // This prevents full page re-renders on tab switch
            if (isNewUser) {
              setProfile(undefined);
            }
            fetchProfile(newSession.user.id)
              .then(async (profileData) => {
                if (!mounted) return;
                // Fetch staff roles from backend and merge into profile
                const staffRoles = await fetchStaffRoles();
                const enrichedProfile = profileData ? { ...profileData, staffRoles } : profileData;
                setProfile(enrichedProfile);
                profileRef.current = enrichedProfile;
                // Identify user in PostHog for analytics and feature flags
                if (profileData) {
                  identifyUser(profileData.id, {
                    email: profileData.email,
                    is_owner: profileData.is_owner,
                    full_name: profileData.full_name ?? null,
                  });
                  identifyCannyUser({
                    id: profileData.id,
                    email: profileData.email,
                    fullName: profileData.full_name ?? "",
                    avatarURL: profileData.profile_photo,
                    createdAt: profileData.created_at,
                  });
                }
              })
              .catch(() => {
                if (!mounted) return;
                setProfile(null);
              });
          }
        }
      } else if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        setProfile(null);
        userRef.current = null;
        profileRef.current = null;
        localStorage.removeItem(PROFILE_CACHE_KEY);
        // Reset PostHog user identity
        resetUser();
      } else if (event === "USER_UPDATED" && newSession) {
        setUser(newSession.user);
        userRef.current = newSession.user;
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, fetchStaffRoles]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm-email-success`,
        data: fullName ? { full_name: fullName } : undefined,
      },
    });

    if (error) {
      // Check for specific error cases
      if (error.message?.includes("User already registered")) {
        throw new Error("An account with this email already exists. Please sign in instead.");
      }
      throw error;
    }

    // Check if user already exists (Supabase returns user but doesn't throw error for existing unconfirmed emails)
    if (data?.user?.identities && data.user.identities.length === 0) {
      throw new Error("An account with this email already exists. Please sign in or check your email for the confirmation link.");
    }

    // The profile will be created automatically by the database trigger
    // No need to manually insert it here

    return { error: null };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      throw error;
    }

    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
    localStorage.removeItem(PROFILE_CACHE_KEY);
    // Reset PostHog user identity
    resetUser();
  };

  const hasStudioAccess = useMemo(
    () => !!(profile?.is_owner || (profile?.staffRoles && profile.staffRoles.length > 0)),
    [profile]
  );

  const value = {
    user,
    profile,
    session,
    loading: loading,
    hasStudioAccess,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
